import {
  HttpContext,
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import * as _ from 'lodash';
import { catchError, Observable, throwError } from 'rxjs';
import { UserEffects } from '../../state/user/user.effects';
import { EAuthProvider, ISSOClient } from '../../state/user/user.interface';
import { IValidationError } from '../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../utilities/snackbar.service';
import { environment } from '../environments/environment';
import { VersionUpdateService } from '../services/version-update.service';

let apiUrl: string | undefined;

type TUrlTarget = 'oeeTracker' | 'actionTracker';
const fullUrlsWithCredentials = ['https://df-sso.scm3d.com'];

export const API_TARGET = new HttpContextToken<TUrlTarget>(() => 'actionTracker');
export const oeeContext = new HttpContext().set(API_TARGET, 'oeeTracker');

export const SKIP_ERROR_HANDLING_HEADER = 'Skip-Error-Message-And-Redirect';

@Injectable()
export class ApiInterceptor implements HttpInterceptor {
  constructor(
    private readonly snackBar: SnackbarService,
    private readonly translate: TranslateService,
    private readonly versionUpdateService: VersionUpdateService,
  ) {}

  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isFullUrl = (url: string): boolean => /^(http|https):\/\//.test(url);
    const isLocalAssets = (url: string): boolean => /\/assets\//.test(url);
    const headers: HttpHeaders = req.headers.set('x-at-version', environment.version);

    if (isFullUrl(req.url) || isLocalAssets(req.url)) {
      const withCredentials = fullUrlsWithCredentials.some((url) => req.url.startsWith(url));

      return next.handle(
        req.clone({
          headers,
          withCredentials,
        }),
      );
    }

    const apiReq: HttpRequest<unknown> = ApiInterceptor.modifyRequest(req);

    return next.handle(apiReq).pipe(
      catchError((error: HttpErrorResponse) => {
        if (req.headers.has(SKIP_ERROR_HANDLING_HEADER)) {
          return throwError(() => error);
        }

        if (error.status === 428) {
          this.versionUpdateService.handleVersionMismatch();
        } else if (error.status === 401) {
          redirectToLogin(_.get(error, 'error.message.0.constraints'));
        } else {
          this.handleErrorMessage(error);
        }

        return throwError(() => error);
      }),
    );
  }

  private handleErrorMessage(err: HttpErrorResponse): void {
    const { message: error }: { message: IValidationError[] } = err.error;

    try {
      error.forEach((message: IValidationError) => {
        Object.keys(message.constraints).forEach((errorKey) => {
          this.translate
            .get(`apiError.${errorKey}`, this.additionalOptions(errorKey, message))
            .subscribe((translatedErrorMessage: string | undefined) => {
              this.snackBar.open(
                translatedErrorMessage !== undefined ? translatedErrorMessage : 'apiError.error',
                ESnackbar.error,
              );
            });
        });
      });
    } catch (e) {
      this.snackBar.open(this.translate.instant('apiError.error'), ESnackbar.error);
    }
  }

  private additionalOptions(errorKey: string, errorMessage: IValidationError): { [key: string]: string } {
    const additionalOptions = {};

    for (const [key, value] of Object.entries(_.get(errorMessage, `contexts.${errorKey}`, {}))) {
      const translateString = `apiErrorMessages.additionalOptions.${errorKey}.${key}`;
      const translatedResult = this.translate.instant(translateString);

      _.set(additionalOptions, key, translateString === translatedResult ? value : translatedResult);
    }

    return additionalOptions;
  }

  private static modifyRequest(req: HttpRequest<unknown>): HttpRequest<unknown> {
    const target: TUrlTarget = req.context.get(API_TARGET) || 'actionTracker';
    const apiDomain: string = getApiUrl(target);
    let { headers } = req;

    if (target === 'actionTracker') {
      headers = headers.set('x-at-version', environment.version);
    }

    return req.clone({
      headers,
      url: `${apiDomain}/${req.url}`,
      withCredentials: true,
    });
  }
}

export const getApiUrl = (target: TUrlTarget = 'actionTracker'): string => {
  if (target === 'oeeTracker') {
    return UserEffects.getUser().moduleUrls.oeeTrackerBackendUrl;
  }

  const { origin } = location;
  const possibleSearchValues = {
    'action-tracker': 'action-tracker-api',
  };
  const regExp = new RegExp(Object.keys(possibleSearchValues).join('|'), 'gi');

  apiUrl = origin.replace(regExp, (match) => {
    return possibleSearchValues[match.toLowerCase() as keyof typeof possibleSearchValues];
  });

  if (!environment.production) {
    apiUrl = `${apiUrl}/api`;
  }

  return apiUrl;
};

function redirectToLogin(ssoClient?: ISSOClient): void {
  if (ssoClient?.authProvider === EAuthProvider.actionTracker || !ssoClient?.authProvider) {
    const redirectSsoUrl = `${getApiUrl()}/auth/redirect-sso`;
    const redirectionUrl = `${redirectSsoUrl}?state=${encodeURIComponent(window.location.href)}`;

    window.location.replace(redirectionUrl);
  } else if (ssoClient?.authProvider === EAuthProvider.digitalFactory) {
    let url = `${environment.ssoUrl + environment.ssoAuthUrl + environment.ssoEnv}?state=${encodeURIComponent(window.location.href)}`;

    if (ssoClient?.loginString) {
      url += `&client=${ssoClient?.loginString}`;
    }

    window.location.replace(url);
  }
}
